import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';
import { SortAsc, SortDesc } from 'lucide-react';
import { useRecruiter } from '../context/RecruiterContext.tsx'; // Import the useRecruiter hook

const Search = () => {
    const location = useLocation();
    const [state, setState] = useState<{ data: any, loading: boolean, error: string | null }>({ data: null, loading: true, error: null });
    const [searchTerm, setSearchTerm] = useState('');
    const prevDataRef = useRef(null);
    const isRecruiter = useRecruiter();
    // get the isRecruiter from the layout



    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const docId = searchParams.get('docId');
        const docType = searchParams.get('docType');
        const historical = searchParams.get('historical');
        console.log("historical", historical);

        if (docType === "job_description" && isRecruiter.isRecruiter === false) {
            setState({ data: null, loading: false, error: 'Unauthorized access - If you think this was an error contact support@cvseeyou.com' });
            return;
        }
        if (docId && docType) {
            const fetchData = async () => {
                try {
                    if (historical === "true") {
                        const response = await fetch(`/api/search?docId=${docId}&docType=${docType}&historical=true`);
                        const data = await response.json();
                        console.log("DATA", data);
                        if (JSON.stringify(prevDataRef.current) !== JSON.stringify(data)) {
                            prevDataRef.current = data;
                            setState({ data, loading: false, error: null });
                        }
                    } else {
                        const response = await fetch(`/api/search?docId=${docId}&docType=${docType}`);
                        const data = await response.json();
                        console.log("DATA", data);
                        if (JSON.stringify(prevDataRef.current) !== JSON.stringify(data)) {
                            prevDataRef.current = data;
                            setState({ data, loading: false, error: null });
                        }
                    }
                } catch (error) {
                    setState({ data: null, loading: false, error: 'Failed to fetch data' });
                }
            };

            fetchData();
        }
    }, [location.search, isRecruiter]);

    const filteredData = useMemo(() => {
        if (!state.data || !state.data.Results) return [];
        return state.data.Results.filter(item => {
            const searchFields = [
                item.ContactDetails.ContactDetails.company ?? '',
                item.ContactDetails.ContactDetails.email ?? '',
                item.ContactDetails.ContactDetails.mobile ?? '',
                item.ContactDetails.ContactDetails.phone ?? '',
                item.ContactDetails.ContactDetails.recruiter_email ?? '',
                item.ContactDetails.ContactDetails.recruiter_name ?? '',
                item.ContactDetails.ContactDetails.recruiter_phone ?? '',
                item.ContactDetails.ContactDetails.website ?? '',
                item.ContactDetails.ContactDetails.linkedin ?? '',
                item.ContactDetails.ContactDetails.github ?? '',
                item.ResultDocumentType ?? '',
                item.resultId ?? '',
            ];

            return searchFields.some(field =>
                field.toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    }, [state.data, searchTerm]);


    // lets create the columns for the resume document type.
    const columns = useMemo(() => {
        if (!state.data) return [];

        if (state.data.DocumentType === "Resumes") {
            return [
                {
                    Header: "Name",
                    accessor: "ContactDetails.ContactDetails.name",
                },
                {
                    Header: "Email",
                    accessor: "ContactDetails.ContactDetails.email",
                },
                {
                    Header: 'Phone',
                    accessor: 'ContactDetails.ContactDetails.phone',
                    Cell: ({ value }: { value: string | number }) => <a href={`tel:${value}`} className="text-blue-500 hover:underline">{value}</a>,
                },
                {
                    Header: "LinkedIn",
                    accessor: "ContactDetails.ContactDetails.linkedin",
                },
                {
                    Header: "Github",
                    accessor: "ContactDetails.ContactDetails.github"
                },
                {
                    Header: 'Match Level',
                    accessor: 'Score',
                    Cell: ({ value }: { value: string | number }) => <MatchLevelCell score={value as number} />,
                },
                {
                    Header: 'Download Document',
                    accessor: 'resultId',
                    Cell: ({ value }: { value: string }) => (
                        <a href={`/api/download?docId=${value}`} className="text-sm text-gray-600" target="_blank" rel="noopener noreferrer">Download Document</a>
                    ),
                },

            ];
        } else {
            return [
                {
                    Header: 'Company',
                    accessor: 'ContactDetails.ContactDetails.company',
                },
                {
                    Header: 'Email',
                    accessor: 'ContactDetails.ContactDetails.email',
                },
                {
                    Header: 'Mobile',
                    accessor: 'ContactDetails.ContactDetails.mobile',
                },
                {
                    Header: 'Phone',
                    accessor: 'ContactDetails.ContactDetails.phone',
                    Cell: ({ value }: { value: string | number }) => <a href={`tel:${value}`} className="text-blue-500 hover:underline">{value}</a>,
                },
                {
                    Header: 'Recruiter Email',
                    accessor: 'ContactDetails.ContactDetails.recruiter_email',
                },
                {
                    Header: 'Recruiter Name',
                    accessor: 'ContactDetails.ContactDetails.recruiter_name',
                },
                {
                    Header: 'Recruiter Phone',
                    accessor: 'ContactDetails.ContactDetails.recruiter_phone',
                },
                {
                    Header: 'Website',
                    accessor: 'ContactDetails.ContactDetails.website',
                },
                {
                    Header: 'Match Level',
                    accessor: 'Score',
                    Cell: ({ value }: { value: string | number }) => <MatchLevelCell score={Number(value)} />,
                },
                {
                    Header: 'Download Document',
                    accessor: 'resultId',
                    Cell: ({ value }: { value: string }) => (
                        <a href={`/api/download?docId=${value}`} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-green-500" target="_blank" rel="noopener noreferrer">Download Document</a>
                    ),
                },
                {
                    Header: 'Apply',
                    accessor: (row: any) => row.resultId ?? '',
                    Cell: ({ value }: { value: string }) => {
                        const [submitted, setSubmitted] = useState(false);

                        return (
                            <>
                                {isRecruiter.isLoggedIn ? (
                                    <button
                                        onClick={async () => {
                                            try {
                                                const response = await fetch(`/api/apply?docId=${value}&user_id=${isRecruiter.userId}`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                    },
                                                });
                                                if (!response.ok) {
                                                    throw new Error(`HTTP error! status: ${response.status}`);
                                                }
                                                const data = await response.json();
                                                console.log('Apply response:', data);
                                                setSubmitted(true);
                                            } catch (error) {
                                                console.error('Error applying:', error);
                                            }
                                        }}
                                        className={`px-4 py-2 rounded ${submitted ? 'bg-gray-400 text-white' : 'bg-green-600 text-white hover:bg-green-500'}`}
                                        disabled={submitted}
                                    >
                                        {submitted ? 'Application Submitted' : 'Apply'}
                                    </button>
                                ) : (
                                    <span className="text-red-500">Login to apply</span>
                                )}
                            </>
                        );
                    },
                },
            ];
        }
    }, [state.data, isRecruiter]);

    const memoizedFilteredData = useMemo(() => filteredData, [filteredData]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data: memoizedFilteredData,
            initialState: {
                sortBy: [{ id: 'Score', desc: true }]
            }
        },
        useSortBy
    );

    if (state.loading) {
        return (
            <div className="rounded-lg bg-white p-6 shadow relative z-50">
                <div className="flex items-center justify-center">
                    <div className="text-black animate-spin rounded-full h-16 w-16 border-4 border-gray-900 border-t-transparent"></div>
                </div>
            </div>
        );
    }

    if (state.error) {
        return (
            <div className="rounded-lg bg-red-50 p-4 text-red-800">
                {state.error}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Search Results</h2>
            </div>
            <div className="p-6">
                <MatchLevelLegend />

                <div className="mb-4">
                    <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Search results..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="overflow-x-auto">
                    <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            {headerGroups.map(headerGroup => (
                                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th
                                            key={column.id}
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center space-x-1">
                                                <span>{column.render('Header')}</span>
                                                {column.isSorted && (
                                                    <span>
                                                        {column.isSortedDesc ?
                                                            <SortDesc className="h-4 w-4" /> :
                                                            <SortAsc className="h-4 w-4" />
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                            {rows.length > 0 ? (
                                rows.map(row => {
                                    prepareRow(row);
                                    return (
                                        <tr key={row.id} {...row.getRowProps()} className="hover:bg-gray-50">
                                            {row.cells.map(cell => (
                                                <td
                                                    key={cell.column.id}
                                                    {...cell.getCellProps()}
                                                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                        No results found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const MatchLevelCell = ({ score }: { score: number }) => {
    const MATCH_LEVELS = [
        { threshold: 0.8, level: 'Good Match', color: 'bg-green-100 text-green-800' },
        { threshold: 0.4, level: 'Partial Match', color: 'bg-yellow-100 text-yellow-800' },
        { threshold: Infinity, level: 'Low Match', color: 'bg-red-100 text-red-800' },
    ];
    const { level, color } = MATCH_LEVELS.find(l => score < l.threshold) || MATCH_LEVELS[2];
    return <span className={`font-medium ${color} px-2 py-1 rounded-full`}>{level}</span>;
};
const MatchLevelLegend = () => (
    <div className="mb-4 flex gap-4">
        {[
            { level: 'Good Match', color: 'bg-green-100 text-green-800' },
            { level: 'Partial Match', color: 'bg-yellow-100 text-yellow-800' },
            { level: 'Low Match', color: 'bg-red-100 text-red-800' },
        ].map(({ level, color }) => (
            <span key={level} className="flex items-center gap-2">
                <span className={`inline-block w-4 h-4 rounded-full ${color}`}></span> {level}
            </span>
        ))}
    </div>
);

export default Search;